import React, { useState } from "react";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { dashApi } from "../api";
import { useDashAuth } from "../AuthContext";
import { formatRelative } from "../utils";

export default function AccountPage() {
  const { user } = useDashAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await dashApi.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (er) {
      toast.error(er.message || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="account-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">Account</h1>
        <p className="text-sm text-slate-500 mt-1">Your profile and security settings.</p>
      </div>

      <Card className="p-5">
        <h2 className="font-heading font-semibold text-slate-900">Profile</h2>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wider text-slate-500">Name</dt>
            <dd className="text-slate-800">{user?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-slate-500">Email</dt>
            <dd className="text-slate-800">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-slate-500">Role</dt>
            <dd className="text-slate-800 capitalize">{user?.role?.replace("_", " ")}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-slate-500">Last sign-in</dt>
            <dd className="text-slate-800">{user?.last_login_at ? formatRelative(user.last_login_at) : "first session"}</dd>
          </div>
        </dl>
      </Card>

      <Card className="p-5">
        <h2 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> Change password
        </h2>
        <form onSubmit={onSubmit} className="mt-4 space-y-3" data-testid="change-password-form">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current-input"
            />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="cp-new-input"
            />
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="cp-confirm-input"
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy} data-testid="cp-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
